import React, { useEffect } from 'react';
import { Home, Search, ArrowLeft, FileText, Globe } from 'lucide-react';
import { useSEOMetaTags } from './SEOMetaTags';

interface NotFoundPageProps {
  onBack?: () => void;
  onNavigate?: (page: string) => void;
}

export const NotFoundPage: React.FC<NotFoundPageProps> = ({ onBack, onNavigate }) => {
  useSEOMetaTags({
    title: '404 - Page Not Found | Orcaseotools',
    description: 'The page you are looking for could not be found. Return to Orcaseotools homepage or explore our SEO and AEO audit tools.',
    canonicalUrl: 'https://orcaseotools.com/404',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "404 - Page Not Found",
      "description": "The requested page could not be found",
      "url": "https://orcaseotools.com/404",
      "inLanguage": "en-US"
    }
  });

  // Set 404 status code indication and analytics
  useEffect(() => {
    // Update document title
    document.title = '404 - Page Not Found | Orcaseotools';
    
    // Try to indicate 404 status (client-side)
    // Note: True 404 status must be set by server
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Log 404 for analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_title: '404 - Page Not Found',
        page_location: window.location.href
      });
    }
  }, []);

  const quickLinks = [
    { icon: Home, label: 'Home', page: 'landing' },
    { icon: Search, label: 'Services', page: 'services' },
    { icon: FileText, label: 'FAQ', page: 'faq' },
    { icon: Globe, label: 'Contact', page: 'contact' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto text-center space-y-8">
        {/* 404 Visual */}
        <div className="space-y-4">
          <h1 className="text-9xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
            404
          </h1>
          <h2 className="text-3xl font-bold text-white">Page Not Found</h2>
          <p className="text-xl text-slate-300 max-w-md mx-auto">
            Oops! The page you're looking for doesn't exist or has been moved.
          </p>
        </div>

        {/* Helpful Message */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 space-y-4">
          <p className="text-slate-300">
            Don't worry! Here are some helpful links to get you back on track:
          </p>
          
          {/* Quick Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <button
                  key={index}
                  onClick={() => {
                    if (onNavigate) {
                      onNavigate(link.page);
                    } else if (onBack) {
                      onBack();
                    }
                  }}
                  className="flex flex-col items-center gap-2 p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-colors group"
                >
                  <Icon size={24} className="text-indigo-400 group-hover:text-indigo-300" />
                  <span className="text-sm text-slate-300 group-hover:text-white">{link.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => {
              if (onNavigate) {
                onNavigate('landing');
              } else if (onBack) {
                onBack();
              } else {
                window.location.href = '/';
              }
            }}
            className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
          >
            <Home size={20} />
            Go to Homepage
          </button>
          <button
            onClick={() => {
              if (onBack) {
                onBack();
              } else {
                window.history.back();
              }
            }}
            className="px-8 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>

        {/* Search Suggestion */}
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
          <p className="text-sm text-slate-400">
            Looking for something specific? Try our{' '}
            <button
              onClick={() => onNavigate ? onNavigate('services') : null}
              className="text-indigo-400 hover:text-indigo-300 underline"
            >
              SEO Tools
            </button>
            {' '}or check out our{' '}
            <button
              onClick={() => onNavigate ? onNavigate('faq') : null}
              className="text-indigo-400 hover:text-indigo-300 underline"
            >
              FAQ
            </button>
            {' '}for answers.
          </p>
        </div>
      </div>
    </div>
  );
};

